import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { Post } from 'src/app/core/models/core.models';
import { NomPostObjectType, NomPostType } from 'src/app/core/constants';
import { makeShadowPostId, realPostId } from './shadow-post.utils';

/**
 * The feed post list's thread derivation and the primitives that maintain it.
 * Used by the posts query's `queryFn` and by every cache patch, so that both
 * produce the same structure. Nothing here reads or writes store state.
 */

const postTypeIconMap: { [key in NomPostType]: IconProp } = {
  [NomPostType.OnlineArticle]: ['fas', 'earth-americas'],
  [NomPostType.FacebookPost]: ['fab', 'facebook'],
  [NomPostType.TwitterPost]: ['fab', 'x-twitter'],
  [NomPostType.YouTubeVideo]: ['fab', 'youtube'],
  [NomPostType.InstagramPost]: ['fab', 'instagram'],
  [NomPostType.LinkedinPost]: ['fab', 'linkedin'],
  [NomPostType.TelegramPost]: ['fab', 'telegram'],
  [NomPostType.BroadcastTVVideo]: ['fas', 'tv'],
  [NomPostType.PrintNewspaperArticle]: ['fas', 'newspaper'],
  [NomPostType.BroadcastRadioAudio]: ['fas', 'radio'],
  [NomPostType.TikTokPost]: ['fab', 'tiktok'],
};

export const postObjectTypeIconMap: Partial<
  Record<NomPostObjectType, IconProp>
> = {
  [NomPostObjectType.Story]: ['fas', 'book-open'],
};

export function getIconUrlForPost(post: Post): IconProp {
  const objectTypeIcon = post.post_object_type_id
    ? postObjectTypeIconMap[post.post_object_type_id as NomPostObjectType]
    : undefined;

  return (
    objectTypeIcon ||
    postTypeIconMap[post.post_type_id] || ['fas', 'earth-americas']
  );
}

/**
 * Turns the server's flat result array into the rendered entry map: the
 * feed-matching post is the canonical entry, and every appearance of it as an
 * ancestor inside another thread becomes a display-only
 * `${realId}__shadow__${leafId}` copy, with `depth` / `is_parent` /
 * `is_collapsed` / `has_parent_thread` and rewired `parent` pointers computed
 * per entry.
 */
export function buildPostThreadMap(posts: Post[]): Map<string, Post> {
  const postMap: Map<string, Post> = new Map();
  const rootPostMap = new Map<string, boolean>();

  if (!posts) {
    return postMap;
  }

  for (const post of posts) {
    rootPostMap.set(post.id, true);
  }

  for (const post of posts) {
    let currentPost: Post | undefined = post;
    const ancestorChain: Post[] = [];

    // Build ancestor chain from leaf (post) up to the topmost parent
    while (currentPost) {
      ancestorChain.unshift(currentPost);
      currentPost = currentPost.parent;
      if (currentPost) {
        currentPost = {
          ...currentPost,
          is_parent: true,
          is_collapsed: false,
        };
      }
    }

    let depth = 0;
    const len = ancestorChain.length;
    let previousParentId: string | null = null;

    // Invariant (independent of result order): the real post id is only
    // ever used for the leaf (feed-matching) entry — the single
    // selectable/editable row. A feed-matching post appearing as an
    // ancestor inside another thread ALWAYS gets a display-only shadow
    // copy (`${realId}__shadow__${leafId}`), never the real entry.
    for (const p of ancestorChain) {
      const isLast = depth === len - 1;
      const isRoot = rootPostMap.has(p.id);
      // Rewire the parent pointer to whatever key the previous chain
      // element was inserted under (possibly a shadow id) so collapse /
      // ancestor walks stay inside this thread's rendered entries.
      const parent: Post | undefined =
        previousParentId !== null
          ? ({ ...p.parent, id: previousParentId } as Post)
          : p.parent;

      if (isLast) {
        // Leaf = the feed-matching post itself: canonical, selectable entry
        postMap.set(p.id, {
          ...p,
          icon: getIconUrlForPost(p),
          depth,
          is_collapsed: false,
          has_parent_thread: depth > 0,
          is_parent: false,
          selected: p.selected,
          parent,
        });
        previousParentId = p.id;
      } else if (isRoot) {
        // Feed-matching post appearing as an ancestor in THIS thread:
        // display-only shadow copy, unique per thread (keyed by leaf id)
        const shadowId = makeShadowPostId(p.id, post.id);
        postMap.set(shadowId, {
          ...p,
          id: shadowId,
          icon: getIconUrlForPost(p),
          depth,
          is_collapsed: false,
          has_parent_thread: depth > 0,
          is_parent: true,
          selected: false,
          parent,
        });
        previousParentId = shadowId;
      } else if (!postMap.has(p.id)) {
        // Pure ancestor (not in the result set), first occurrence
        postMap.set(p.id, {
          ...p,
          icon: getIconUrlForPost(p),
          depth,
          is_collapsed: false,
          has_parent_thread: depth > 0,
          is_parent: true,
          selected: false,
          parent,
        });
        previousParentId = p.id;
      } else {
        // Pure ancestor appearing in multiple threads - update depth to
        // the shallowest and keep parent status
        const existing = postMap.get(p.id)!;
        postMap.set(p.id, {
          ...existing,
          depth:
            typeof existing.depth === 'number'
              ? Math.min(existing.depth, depth)
              : depth,
          is_parent: true,
          has_parent_thread: existing.has_parent_thread || depth > 0,
          parent:
            previousParentId !== null
              ? ({ ...existing.parent, id: previousParentId } as Post)
              : existing.parent,
        });
        previousParentId = p.id;
      }

      depth++;
    }
  }

  return postMap;
}

/**
 * The response's first post, read back off the derived map: entries are
 * inserted thread by thread in response order and only a feed-matching leaf gets
 * `is_parent: false`, so the first such entry is `response.posts[0]`.
 *
 * It seeds the details pane. Reading it back off the map keeps that map the
 * single representation, instead of caching the raw array beside it.
 */
export function firstCanonicalPost(posts: Map<string, Post>): Post | null {
  for (const post of posts.values()) {
    if (!post.is_parent) return post;
  }

  return null;
}

/**
 * Merges a backend post update into the canonical entry AND every
 * display-only shadow copy of it (`${realId}__shadow__*`), so all rendered
 * occurrences of the post stay in sync. Preserves the derived
 * thread/display fields (depth, is_parent, parent pointer, icon, …) and the
 * client-side `content`/`selected` state, which the backend payload must not
 * clobber.
 */
export function mergePostUpdate(posts: Map<string, Post>, updated: Post): void {
  const realId = realPostId(updated.id);
  const shadowPrefix = makeShadowPostId(realId, '');

  for (const key of posts.keys()) {
    if (key !== realId && !key.startsWith(shadowPrefix)) continue;

    const existing = posts.get(key);
    if (!existing) continue;

    posts.set(key, {
      ...existing,
      ...updated,
      // Entry-specific / derived fields
      id: key,
      content: existing.content,
      selected: existing.selected,
      icon: existing.icon,
      depth: existing.depth,
      parent: existing.parent,
      is_parent: existing.is_parent,
      is_collapsed: existing.is_collapsed,
      has_parent_thread: existing.has_parent_thread,
      parent_thread_index: existing.parent_thread_index,
    });
  }
}

/**
 * Walks a rendered entry's `parent` chain, which the derivation rewired to the
 * keys the ancestors were inserted under — so this stays inside one thread.
 */
export function collectAncestorIds(
  posts: Map<string, Post>,
  startId: string,
): string[] {
  const start = posts.get(startId);
  if (!start) return [];

  const ids: string[] = [];
  let cursor = start.parent;

  while (cursor) {
    ids.push(cursor.id);
    cursor = cursor.parent;
  }
  return ids.reverse();
}

export function setAncestorsCollapsed(
  posts: Map<string, Post>,
  ancestorIds: string[],
  isCollapsed: boolean,
) {
  let index = 0;
  for (const id of ancestorIds) {
    const p = posts.get(id);
    if (!p) continue;

    posts.set(id, {
      ...p,
      is_parent: true,
      has_parent_thread: !!p.parent,
      is_collapsed: isCollapsed,
      parent_thread_index: index,
    });
    index++;
  }
}

/**
 * Collapse / expand the whole ancestor chain of one rendered entry. Mutates the
 * map in place.
 */
export function togglePostThread(
  posts: Map<string, Post>,
  postId: string,
): void {
  const ancestorIds = collectAncestorIds(posts, postId);

  // check if at least one ancestor is expanded
  const anyOpen = ancestorIds.some(
    (id) => posts.get(id)?.is_collapsed === false,
  );

  // if any is open → collapse all; if all are closed → expand all
  setAncestorsCollapsed(posts, ancestorIds, anyOpen ? true : false);

  // when expanding, also ensure the clicked post is selected/visible
  const leaf = posts.get(postId);
  if (leaf) {
    posts.set(postId, {
      ...leaf,
      is_collapsed: false,
      parent_thread_expanded: !anyOpen,
    });
  }
}
